import {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {PlusIcon, TrashIcon} from "@heroicons/react/24/outline";
import {User} from "../../../../../api/accessControl/User.ts";
import {BackendApi} from "../../../../../api/api.ts";
import Shell from "../../../../../components/specific/Shell.tsx";
import {PrimaryButton} from "../../../../../components/general/PrimaryButton.tsx";
import Popup from "../../../../../components/general/Popup.tsx";
import {useNotifications} from "../../../../../lib/NotificationContext.tsx";
import {extractApiErrorMessage} from "../../../../../lib/apiErrors.ts";
import {useCustomers} from "../../../../../hooks/useCustomers.ts";
import {InvoicePreviewSeller} from "../CreateInvoicePage/InvoicePreview.tsx";
import {RecurringInvoicesList} from "./RecurringInvoicesList.tsx";
import {RecurringSeriesPreviewDrawer} from "./RecurringSeriesPreviewDrawer.tsx";
import {
    deleteRecurringSeries,
    listRecurringSeries,
    setRecurringSeriesActive,
} from "./recurringInvoicesApi.ts";
import {RecurringSeriesDetails} from "./types/recurringInvoice.ts";

const FOOTER_TEXT_SETTING = 'SALES_INVOICE_FOOTER_TEXT'
const INVOICE_CONTACT_EMAIL_SETTING = 'SALES_INVOICE_CONTACT_EMAIL'

function sortSeries(series: RecurringSeriesDetails[]): RecurringSeriesDetails[] {
    return [...series].sort((a, b) => {
        if (a.is_paused !== b.is_paused) return a.is_paused ? 1 : -1
        if (a.next_run_on !== b.next_run_on) return a.next_run_on < b.next_run_on ? -1 : 1
        return a.customer_name.localeCompare(b.customer_name)
    })
}

export default function RecurringInvoicesPage({currentUser}: { currentUser: User }) {
    const api = useMemo(() => new BackendApi(currentUser.getToken()), [currentUser])
    const navigate = useNavigate()
    const {dispatch} = useNotifications()
    const {customers} = useCustomers(currentUser)

    const [series, setSeries] = useState<RecurringSeriesDetails[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [busySeriesIds, setBusySeriesIds] = useState<string[]>([])

    const [previewSeries, setPreviewSeries] = useState<RecurringSeriesDetails | null>(null)
    const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false)
    const [seriesPendingDeletion, setSeriesPendingDeletion] = useState<RecurringSeriesDetails | null>(null)

    const [business, setBusiness] = useState<{
        name?: string | null,
        website?: string | null,
        registration_number?: string | null,
        vat_number?: string | null,
    } | null>(null)
    const [logoDataUri, setLogoDataUri] = useState<string | null>(null)

    async function loadSeries() {
        try {
            setSeries(sortSeries(await listRecurringSeries(api)))
        } catch (e) {
            console.error('Kon terugkerende facturen niet laden:', e)
            dispatch({
                title: 'Laden mislukt',
                description: 'De terugkerende facturen konden niet worden geladen. Probeer het opnieuw.',
                type: 'error',
            })
        } finally {
            setIsLoading(false)
        }
    }

    async function loadSellerDetails() {
        try {
            await currentUser.businessSettings.refresh()
            setBusiness(await api.Business__myBusiness({}))
        } catch (e) {
            console.error('Kon bedrijfsgegevens niet laden:', e)
        }

        try {
            const logo = await api.Me__business__logo({})
            setLogoDataUri(`data:${logo.content_type};base64,${logo.file_data}`)
        } catch {
            setLogoDataUri(null)
        }
    }

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- async data load, same pattern as the rest of the app
        loadSeries()
        loadSellerDetails()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser])

    function settingString(key: string): string {
        try {
            const value = currentUser.businessSettings.getSetting(key)
            return typeof value === 'string' ? value : ''
        } catch {
            return ''
        }
    }

    function markBusy(seriesId: string, isBusy: boolean) {
        setBusySeriesIds(previous => isBusy
            ? [...previous, seriesId]
            : previous.filter(id => id !== seriesId))
    }

    async function handleToggleActive(row: RecurringSeriesDetails, isActive: boolean) {
        markBusy(row.id, true)
        try {
            const updated = await setRecurringSeriesActive(api, row.id, isActive)
            setSeries(previous => sortSeries(
                previous.map(candidate => candidate.id === updated.id ? updated : candidate),
            ))
            dispatch({
                title: isActive ? 'Reeks geactiveerd' : 'Reeks gepauzeerd',
                description: isActive
                    ? `Er wordt weer automatisch gefactureerd voor ${row.customer_name}.`
                    : `Er worden geen nieuwe facturen meer aangemaakt voor ${row.customer_name}.`,
                type: 'success',
            })
        } catch (e) {
            console.error('Kon de reeks niet bijwerken:', e)
            dispatch({
                title: 'Bijwerken mislukt',
                description: extractApiErrorMessage(e) ?? 'De reeks kon niet worden bijgewerkt.',
                type: 'error',
            })
        } finally {
            markBusy(row.id, false)
        }
    }

    async function handleConfirmDelete() {
        const row = seriesPendingDeletion
        if (!row) return

        setSeriesPendingDeletion(null)
        markBusy(row.id, true)
        try {
            await deleteRecurringSeries(api, row.id)
            setSeries(previous => previous.filter(candidate => candidate.id !== row.id))
            dispatch({
                title: 'Reeks verwijderd',
                description: `Er worden geen facturen meer klaargezet voor ${row.customer_name}. `
                    + 'Al aangemaakte facturen blijven staan.',
                type: 'success',
            })
        } catch (e) {
            console.error('Kon de reeks niet verwijderen:', e)
            dispatch({
                title: 'Verwijderen mislukt',
                description: extractApiErrorMessage(e) ?? 'De reeks kon niet worden verwijderd.',
                type: 'error',
            })
        } finally {
            markBusy(row.id, false)
        }
    }

    const previewCustomer = customers.find(candidate => candidate.id === previewSeries?.customer_id)

    const previewSeller: InvoicePreviewSeller = {
        businessName: business?.name ?? currentUser.getBusinessName() ?? '',
        streetWithHouseNumber: [
            settingString('BUSINESS_ADDRESS_STREET_NAME'),
            settingString('BUSINESS_ADDRESS_HOUSE_NUMBER'),
        ].filter(Boolean).join(' '),
        postalCode: settingString('BUSINESS_ADDRESS_POSTAL_CODE'),
        city: settingString('BUSINESS_ADDRESS_CITY_NAME'),
        website: business?.website ?? null,
        kvkNumber: business?.registration_number ?? null,
        vatNumber: business?.vat_number ?? null,
        contactName: currentUser.getFullName(),
        contactEmail: settingString(INVOICE_CONTACT_EMAIL_SETTING) || currentUser.email,
        logoDataUri,
        iban: null,
        ibanOwnerName: null,
    }

    return (
        <Shell
            currentUser={currentUser}
            title="Terugkerende facturen"
            subtitle="Facturen die automatisch op een vast ritme worden klaargezet."
            titleActions={
                <PrimaryButton
                    title="Nieuwe reeks"
                    icon={PlusIcon}
                    onClick={() => navigate('/create-invoice')}
                />
            }
        >
            <RecurringInvoicesList
                series={series}
                isLoading={isLoading}
                busySeriesIds={busySeriesIds}
                onOpenPreview={(row) => {
                    setPreviewSeries(row)
                    setIsPreviewOpen(true)
                }}
                onToggleActive={handleToggleActive}
                onDelete={setSeriesPendingDeletion}
                onCreateNew={() => navigate('/create-invoice')}
            />

            <RecurringSeriesPreviewDrawer
                open={isPreviewOpen}
                updateOpenState={setIsPreviewOpen}
                series={previewSeries}
                seller={previewSeller}
                buyerStreetWithHouseNumber={[
                    previewCustomer?.address.street,
                    previewCustomer?.address.house_number,
                ].filter(Boolean).join(' ')}
                buyerPostalCode={previewCustomer?.address.postal_code ?? ''}
                buyerCity={previewCustomer?.address.city ?? ''}
                footerText={settingString(FOOTER_TEXT_SETTING)}
            />

            <Popup
                open={seriesPendingDeletion !== null}
                onClose={() => setSeriesPendingDeletion(null)}
                onConfirm={handleConfirmDelete}
                title="Terugkerende factuur verwijderen?"
                Icon={TrashIcon}
                variant="danger"
                confirmButtonDisplayLabel="Verwijderen"
            >
                Er worden geen nieuwe facturen meer klaargezet
                voor {seriesPendingDeletion?.customer_name}. Facturen die al zijn aangemaakt
                blijven in je administratie staan.
            </Popup>
        </Shell>
    )
}
